import React, { useState } from 'react'
import styles from './TRouteHills.scss'
import { FullBleedList } from 'common/FullBleedList'
import { Distance, Elevation, Grade } from 'common/units'
import { featureTypes } from 'common/MapV2/featureTypes'
import { findAscents, findDescents } from 'lib/insights/hill_finder'
import { Msg, commonNouns, commonMessages } from 'lib/localization'
import { TrackLocation } from 'data/records'
import _ from 'lodash'
import { injectIntl } from 'react-intl'

const onIdle = window.requestIdleCallback || window.requestAnimationFrame

const findHills = _.memoize((trackPoints) => new Promise((resolve, reject) => {
  onIdle(() => {
    // FIXME this mc should happen at trackpoint deserialization, but putting
    // it here for now to patch the issue of troute trackPoints being dirty
    const points = window.rwgps.TrackPoint.fromJSON(trackPoints).trackPoints
    const data = new window.rwgps.Data(points)
    const mc = new window.rwgps.MetricsCalculator(data, false)
    mc.calculate()
    const mcPoints = mc._points

    if (!mcPoints || mcPoints.length === 0) {
      resolve({
        climbs: [],
        descents: []
      })
    }
    resolve({
      climbs: findAscents(mcPoints),
      descents: findDescents(mcPoints)
    })
  })
}))

const getHillMetrics = (rawPoints, hill) => new Promise((resolve, reject) => {
  onIdle(() => {
    const tpsResult = window.rwgps.TrackPoint.fromJSON(rawPoints)
    const points = tpsResult.trackPoints
    const data = new window.rwgps.Data(points)
    const dataSlice = data.slice(hill.first_i, hill.last_i - hill.first_i + 1)
    const mc = new window.rwgps.MetricsCalculator(dataSlice, false)
    const metrics = mc.calculate()
    resolve(metrics)
  })
})

const HillList = ({ hills, rawPoints, title, eleKey, onFeatureHovered, onFeatureSelected }) => {
  const [expanded, setExpanded] = useState(false)
  const toShow = expanded ? hills : hills.slice(0, 3)

  return (
    <div className={styles.HillList}>
      <h2>{title}</h2>
      <FullBleedList interactive expanded={expanded}>
        {toShow.map(h => <HillListRow key={h.first_i}
          onFeatureHovered={onFeatureHovered}
          onFeatureSelected={onFeatureSelected}
          rawPoints={rawPoints}
          hill={h}
          eleKey={eleKey} />)}
      </FullBleedList>
      {hills.length > 3 && <div className={styles.expandlink}>
        <a onClick={() => setExpanded(!expanded)}>
          {expanded
            ? <Msg m={commonMessages.collapse} values={{ list: title }} />
            : <Msg m={commonMessages.showAll} values={{ list: title }} />}
        </a>
      </div>}
    </div>
  )
}

// We have to run the metrics calculator for hill segments because of the
// way it does *fancy* grade and elevation calculations, accounting for
// data outliers, Garmin garbage, etc etc.
//
// This component allows us to (1) only run the metrics calculator for visible
// Hills, and (2) run it in a requestIdleCallback so that it doesn't block user
// operations like expanding the hill list.
class HillListRow extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      hillMetrics: null
    }
  }

  componentDidMount () {
    const { rawPoints, hill } = this.props
    getHillMetrics(rawPoints, hill)
      .then(hillMetrics => this.setState({ hillMetrics }))
  }

  feature = () => {
    const { hill } = this.props
    return {
      type: featureTypes.HILL,
      firstI: hill.first_i,
      data: {
        highlightSlice: [
          TrackLocation({ left: hill.first_i, pct: 0 }),
          TrackLocation({ left: hill.last_i, pct: 0 })
        ],
        hill
      }
    }
  }

  onClick = () => {
    const { onFeatureSelected } = this.props
    onFeatureSelected(this.feature())
  }

  onMouseOver = () => {
    const { onFeatureHovered } = this.props
    onFeatureHovered(this.feature())
  }

  onMouseOut = () => {
    const { onFeatureHovered } = this.props
    onFeatureHovered(null)
  }

  render () {
    const { hillMetrics } = this.state
    const { hill, eleKey } = this.props
    return (
      <li onMouseOver={this.onMouseOver} onMouseOut={this.onMouseOut} onClick={this.onClick}>
        <div>{hillMetrics ? <Distance value={hillMetrics.distance} short bigUnits /> : '--'}</div>
        <div><Elevation value={hillMetrics ? hillMetrics[eleKey] : hill.delta_e} short /></div>
        <div>{hillMetrics ? <Grade value={hillMetrics.grade.avg} short /> : '--'}</div>
      </li>
    )
  }
}

class _TRouteHills extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      hills: null
    }
  }

  componentDidMount () {
    const { troute } = this.props
    findHills(troute.rawPoints)
      .then(hills => this.setState({ hills }))
  }

  render () {
    const { hills } = this.state
    const { troute, onFeatureHovered, onFeatureSelected, intl } = this.props
    // no loading state, just pop into existience when calc finishes
    if (!hills) { return null }
    return (
      <div className={styles.TRouteHills}>
        {hills.climbs.length > 0 && <HillList
          title={_.capitalize(intl.formatMessage(commonNouns.climbs, { count: 0 }))}
          rawPoints={troute.rawPoints}
          hills={hills.climbs}
          eleKey='ele_gain'
          onFeatureHovered={onFeatureHovered}
          onFeatureSelected={onFeatureSelected} />}
        {hills.descents.length > 0 && <HillList
          title={_.capitalize(intl.formatMessage(commonNouns.descents, { count: 0 }))}
          rawPoints={troute.rawPoints}
          hills={hills.descents}
          eleKey='ele_loss'
          onFeatureHovered={onFeatureHovered}
          onFeatureSelected={onFeatureSelected} />}
      </div>
    )
  }
}

export const TRouteHills = injectIntl(_TRouteHills)
